import React, { useState, useEffect } from "react";
import { isValidPhoneNumber, isValidString } from "../../../utils/string";
import { isAPISuccessful } from "../../../utils/apiHelpers";
import { createReferral } from "../../../api/home";
import { ErrorCodes } from "./types";
import { logReferralError, logReferralSuccess, logReferNowButtonClick } from "../analytics";
import { VALIDATION_MESSAGES } from "../../../utils/validations";

const getStorageKey = (userId: string | null): string => {
    return userId ? `referral_selected_city_${userId}` : "referral_selected_city";
};

const getSavedCity = (userId: string | null): string => {
    if (typeof window === "undefined") return "";
    const storageKey = getStorageKey(userId);
    const savedCity = localStorage.getItem(storageKey);
    return savedCity || "";
};

const saveCityToStorage = (userId: string | null, city: string): void => {
    if (typeof window === "undefined") return;
    const storageKey = getStorageKey(userId);
    if (city) {
        localStorage.setItem(storageKey, city);
    } else {
        localStorage.removeItem(storageKey);
    }
};

export const useReferralForm = () => {
    const [contactNumber, setContactNumber] = useState("");
    const [contactName, setContactName] = useState("");
    const [selectedCity, setSelectedCity] = useState<string>("");
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>("");

    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showFailureModal, setShowFailureModal] = useState(false);
    const [shouldRetryOnError, setShouldRetryOnError] = useState(false);
    const [errorCode, setErrorCode] = useState<string>("");

    // Validation state
    const [touchedFields, setTouchedFields] = useState({
        contactNumber: false,
        contactName: false,
        city: false,
    });
    const [submitAttempted, setSubmitAttempted] = useState(false);

    // Load saved city from localStorage on mount
    useEffect(() => {
        const userId = localStorage.getItem("user_id");
        const savedCity = getSavedCity(userId);
        if (savedCity) {
            setSelectedCity(savedCity);
        }
    }, []);

    const resetForm = () => {
        setContactNumber("");
        setContactName("");
        setTouchedFields({
            contactNumber: false,
            contactName: false,
            city: false,
        });
        setSubmitAttempted(false);
    };

    const handleCityChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedCity(event.target.value);
        setTouchedFields((prev) => ({ ...prev, city: true }));
    };

    const handleContactNumberChange = (value: string) => {
        setContactNumber(value);
        setTouchedFields((prev) => ({ ...prev, contactNumber: true }));
    };

    const handleContactNameChange = (value: string) => {
        setContactName(value);
        setTouchedFields((prev) => ({ ...prev, contactName: true }));
    };

    // Validation functions
    const getContactNumberError = (): string => {
        const shouldShowError = touchedFields.contactNumber || submitAttempted;
        if (!shouldShowError) return "";

        if (!contactNumber || contactNumber.trim() === "") {
            return VALIDATION_MESSAGES.PHONE_NUMBER_REQUIRED;
        }
        if (!isValidPhoneNumber(contactNumber)) {
            return VALIDATION_MESSAGES.PHONE_NUMBER_INVALID;
        }
        return "";
    };

    const getContactNameError = (): string => {
        const shouldShowError = touchedFields.contactName || submitAttempted;
        if (!shouldShowError) return "";

        if (!contactName || contactName.trim() === "") {
            return VALIDATION_MESSAGES.CONTACT_NAME_REQUIRED;
        }
        const nameRegex = /^[a-zA-Z\s]{1,50}$/;
        if (!nameRegex.test(contactName)) {
            return VALIDATION_MESSAGES.CONTACT_NAME_INVALID;
        }
        return "";
    };

    const getCityError = (): string => {
        const shouldShowError = touchedFields.city || submitAttempted;
        if (!shouldShowError) return "";

        if (!selectedCity || selectedCity.trim() === "") {
            return VALIDATION_MESSAGES.CITY_REQUIRED;
        }
        return "";
    };

    // Check if form is actually valid (regardless of error display)
    const isFormValid = (): boolean => {
        if (!contactNumber || contactNumber.trim() === "" || !isValidPhoneNumber(contactNumber)) {
            return false;
        }
        if (!contactName || contactName.trim() === "") {
            return false;
        }
        const nameRegex = /^[a-zA-Z\s]{1,50}$/;
        if (!nameRegex.test(contactName)) {
            return false;
        }
        if (!selectedCity || selectedCity.trim() === "") {
            return false;
        }
        return true;
    };

    const handleReferClick = async () => {
        if (isLoading) return;

        // Mark submit as attempted to show all validation errors
        setSubmitAttempted(true);

        // Check if form is actually valid before submitting
        if (!isFormValid()) {
            return;
        }

        // Log analytics only when form is valid
        logReferNowButtonClick({
            name: contactName,
            city_id: selectedCity,
        });

        setIsLoading(true);

        try {
            const response = await createReferral({
                city_id: selectedCity,
                phone_number: contactNumber,
                name: contactName,
            });

            if (isAPISuccessful(response)) {
                logReferralSuccess(
                    {
                        name: contactName,
                        city_id: selectedCity,
                    },
                    response?.data || {}
                );

                // Save selected city to localStorage on successful submission
                const userId = localStorage.getItem("user_id");
                saveCityToStorage(userId, selectedCity);
                resetForm();
                setShowSuccessModal(true);
            } else {
                const responseErrorCode = response?.data?.error_code;
                const errorMessageResponse = response?.data?.error_message;

                logReferralError(
                    responseErrorCode || "",
                    response?.data || {},
                    {
                        name: contactName,
                        city_id: selectedCity,
                    }
                );

                const isBusinessLogicFailure = responseErrorCode === ErrorCodes.REFEREE_EXISTS;

                if (isBusinessLogicFailure) {
                    resetForm();
                }

                setErrorCode(responseErrorCode || "");
                setErrorMessage(errorMessageResponse || "Something went wrong!");
                setShouldRetryOnError(!isBusinessLogicFailure);
                setShowFailureModal(true);
            }
        } catch (error) {
            logReferralError(
                error instanceof Error ? error.message : "UNKNOWN",
                { error: error },
                {
                    name: contactName,
                    city_id: selectedCity,
                }
            );

            setErrorMessage("Something went wrong!");
            setShouldRetryOnError(true);
            setShowFailureModal(true);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCloseSuccessModal = () => {
        setShowSuccessModal(false);
    };

    const handleCloseFailureModal = () => {
        setShowFailureModal(false);
        setErrorMessage("");
    };

    const isButtonDisabled =
        isLoading || !(isValidPhoneNumber(contactNumber) && isValidString(contactName) && isValidString(selectedCity));

    return {
        contactNumber,
        contactName,
        selectedCity,
        isLoading,
        isButtonDisabled,
        setContactNumber: handleContactNumberChange,
        setContactName: handleContactNameChange,
        handleCityChange,
        showSuccessModal,
        showFailureModal,
        errorMessage,
        errorCode,
        shouldRetryOnError,
        handleReferClick,
        handleCloseSuccessModal,
        handleCloseFailureModal,
        getContactNumberError,
        getContactNameError,
        getCityError,
    };
};
